
import { useCallback, useMemo, useState } from 'react';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import { useAiAssistantSettings } from '@/hooks/use-ai-assistant-settings';
import { useAiAssistantCore } from '@/hooks/useAiAssistantCore';
import { useMessageStateAndPersistence } from '@/hooks/useMessageStateAndPersistence';
import { useAiAssistantState } from '@/hooks/useAiAssistantState';
import { useSessionManagement } from '@/hooks/useSessionManagement';
import { useAutoLoadSession } from '@/hooks/useAutoLoadSession';
import { useMessageOrchestration } from '@/hooks/useMessageOrchestration';
import { useMessageInteractions } from '@/hooks/useMessageInteractions';
import { useAiAssistantEffects } from '@/hooks/useAiAssistantEffects';
import { useUsageStateManager } from '@/hooks/useUsageStateManager';
import { logger } from '@/utils/production-logger';
import { STABLE_CONFIGS } from '@/constants/ai-assistant-context-constants';
import { providerConfig } from './AiAssistantProviderConfig';

/**
 * Hook to manage all dependencies for AiAssistantProvider
 * This centralizes all the complex hook orchestration logic
 */
export const useAiAssistantProviderDependencies = () => {
  // ==================== ALL CORE HOOKS AT TOP LEVEL ====================
  const { user } = useAuth();
  const { language } = useLanguage();
  const { lastSessionId, userMetadata, updateLastSession } = useAiAssistantSettings();

  // Usage update trigger state
  const [usageUpdateTrigger, setUsageUpdateTrigger] = useState<number>(0);

  // ==================== USAGE STATE MANAGEMENT - TOP LEVEL ====================
  const {
    usageStats,
    isLoading: usageLoading,
    refreshUsageStats,
    incrementUsageOptimistically,
    getUsageForTopic
  } = useUsageStateManager({
    userId: user?.id || null,
    onError: (error) => {
      logger.error('Usage state manager error', {
        error: error.message,
        userId: user?.id,
        component: STABLE_CONFIGS.componentName
      });
    }
  });

  // ==================== USAGE CALLBACKS - TOP LEVEL ====================
  const handleUsageUpdate = useCallback(() => {
    // Trigger immediate update for all usage displays
    setUsageUpdateTrigger(prev => prev + 1);
    // Background sync to ensure accuracy
    refreshUsageStats();
  }, [user?.id, refreshUsageStats]);

  const handleOptimisticUsageUpdate = useCallback((topic: string) => {
    incrementUsageOptimistically(topic as any);
    setUsageUpdateTrigger(prev => prev + 1);
  }, [user?.id, incrementUsageOptimistically]);

  // ==================== AI ASSISTANT STATE - TOP LEVEL ====================
  const { isLoading, isTyping, setIsTyping, createSendMessageHandler } = useAiAssistantState();

  // ==================== MESSAGE STATE AND PERSISTENCE - TOP LEVEL ====================
  const {
    messages,
    setMessages,
    clearMessages,
    addUserMessage,
    addAiMessage,
    addFallbackMessage,
    removeMessage,
    updateMessage,
    saveMessage: originalSaveMessage
  } = useMessageStateAndPersistence({
    onMessagesChange: useCallback((newMessages) => {
      // Messages updated
    }, []),
    onSaveError: useCallback((error: Error, messageId: string, requestId?: string) => {
      logger.error('Message save error in context', {
        error: error.message,
        messageId,
        requestId,
        component: STABLE_CONFIGS.componentName
      });
    }, [])
  });

  // Create save message wrapper
  const saveMessage = useCallback(async (message: any) => {
    await originalSaveMessage(message);
  }, [originalSaveMessage]);

  // ==================== CORE FUNCTIONALITY - TOP LEVEL ====================
  const {
    consolidatedErrorHandlers,
    stateCoordination,
    startMessagePerformance,
    finishMessagePerformance
  } = useAiAssistantCore({
    userId: user?.id || null,
    currentSessionId: null, // Will be set after session management
    addFallbackMessage,
    removeMessage
  });

  // ==================== SESSION MANAGEMENT - TOP LEVEL ====================
  const {
    sessions,
    currentSession,
    fetchingSessions,
    createNewSession,
    deleteSession,
    updateSessionTitle,
    selectSession,
    searchSessions,
    loadSession,
    setCurrentSession,
    createNewConversation
  } = useSessionManagement({
    onSessionChange: useCallback((session) => {
      // Session changed
    }, []),
    onMessagesChange: useCallback((newMessages) => {
      if (typeof newMessages === 'function') {
        setMessages(newMessages);
      } else {
        setMessages(newMessages);
      }
    }, [setMessages])
  });

  // ==================== AUTO-LOAD SESSION - TOP LEVEL ====================
  useAutoLoadSession({
    userId: user?.id || null,
    currentSession,
    lastSessionId,
    fetchingSessions,
    userMetadata,
    loadSession,
    updateLastSession,
    onError: consolidatedErrorHandlers.createCentralizedErrorCallback('auto_load_session')
  });

  // ==================== MESSAGE ORCHESTRATION - TOP LEVEL ====================
  const {
    handleMessageFlow,
    validateAndIncrementUsage,
    processAiRequest,
    usageUpdateTrigger: orchestrationUsageTrigger
  } = useMessageOrchestration({
    currentSessionId: currentSession?.id || null,
    userId: user?.id || null,
    language,
    addUserMessage,
    saveMessage,
    addAiMessage,
    updateMessage,
    startMessagePerformance,
    finishMessagePerformance,
    recoverFromError: consolidatedErrorHandlers.recoverFromError,
    messages,
    currentTopic: currentSession?.topic || 'guide',
    onUsageLimitError: consolidatedErrorHandlers.handleUsageLimitError,
    onAiServiceError: consolidatedErrorHandlers.handleAiServiceError,
    onUsageUpdate: handleUsageUpdate,
    setIsTyping
  });

  // ==================== USER INTERACTIONS - TOP LEVEL ====================
  const {
    updateMessageFeedback,
    loadFeedbackForMessages,
    loadMoreMessages,
    resetPagination,
    setTotalMessageCount,
    getMessageCount,
    hasMoreMessages,
    isLoadingMoreMessages,
    isUpdatingFeedback
  } = useMessageInteractions({
    currentSessionId: currentSession?.id || null,
    userId: user?.id || null,
    messages,
    setMessages,
    onMessagesChange: useCallback((messagesUpdater) => {
      if (typeof messagesUpdater === 'function') {
        setMessages(messagesUpdater);
      } else {
        setMessages(messagesUpdater);
      }
    }, [currentSession?.id, setMessages])
  });

  // ==================== EFFECTS & COORDINATION - TOP LEVEL ====================
  useAiAssistantEffects({
    currentSessionId: currentSession?.id || null,
    messagesLength: messages.length,
    resetPagination,
    setTotalMessageCount,
    stateCoordination
  });

  // ==================== ENHANCED SEND MESSAGE HANDLER ====================
  const enhancedSendMessage = useCallback(async (options: any) => {
    // Optimistic update before sending
    if (currentSession?.topic) {
      handleOptimisticUsageUpdate(currentSession.topic);
    }
    
    // Send message using the original handler
    const originalSendMessage = createSendMessageHandler(handleMessageFlow);
    await originalSendMessage(options);
  }, [currentSession?.topic, handleOptimisticUsageUpdate, createSendMessageHandler, handleMessageFlow]);

  // Dependencies initialized successfully (removed excessive logging)

  // ==================== MEMOIZED DEPENDENCIES OBJECT ====================
  return useMemo(() => ({
    // Session management
    createNewSession,
    deleteSession,
    updateSessionTitle,
    selectSession,
    searchSessions,
    loadSession,
    setCurrentSession,
    createNewConversation,
    
    // Message interactions
    clearMessages,
    loadMoreMessages,
    updateMessageFeedback,
    
    // Dynamic state
    messages,
    currentSession,
    sessions,
    isLoading,
    isTyping,
    fetchingSessions,
    hasMoreMessages,
    isLoadingMoreMessages,
    
    // Usage state (cached at context level)
    usageStats,
    usageLoading,
    getUsageForTopic,
    
    // Actions
    sendMessage: enhancedSendMessage,
    usageUpdateTrigger
  }), [
    createNewSession,
    deleteSession,
    updateSessionTitle,
    selectSession,
    searchSessions,
    loadSession,
    setCurrentSession,
    createNewConversation,
    clearMessages,
    loadMoreMessages,
    updateMessageFeedback,
    messages,
    currentSession,
    sessions,
    isLoading,
    isTyping,
    fetchingSessions,
    hasMoreMessages,
    isLoadingMoreMessages,
    usageStats,
    usageLoading,
    getUsageForTopic,
    enhancedSendMessage,
    usageUpdateTrigger
  ]);
};
